import React, { useState, useEffect, Fragment, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import {
  printOrder,
  validateCartContents
} from "utils"
import {
  KpFail,
  KpSuccess,
  CartStockValidationResultModal,
  Button
} from "components"
import { addUser, sendOrder } from "services"
import { deleteAllOrderItems } from "@redux"
import KcCustomerMobileModal from "./KcCustomerMobileModal"
import classes from "./KcPlaceOrderButton.module.scss"

const KcPlaceOrderButton = () => {
  const [t] = useTranslation()

  const dispatch = useDispatch()

  const order =
    useSelector(state => state.order)

  const accountId =
    useSelector(state => state.userAccountInfo._id)

  const shiftCashier =
    useSelector(state => state.shiftCashier)

  const {
    areaName,
    deliveryType,
    deliveryAddress,
    cinemaSolonNumber
  } = useSelector(state => state.deliveryInfo)

  const cashierBranchId =
    useSelector(state => state.userAccountInfo.branchId)

  const temporaryOff =
    useSelector(state => state.systemSettings.temporaryOff)

  const [orderLink, setOrderLink] = useState("")
  const [orderCode, setOrderCode] = useState("")
  const [sending, setSending] = useState(false)
  const [resultModal, setResultModal] = useState("")
  const [problematicItems, setProblematicItems] = useState([])
  const [openCartValidationResultModal, setOpenCartValidationResultModal] = useState(false)
  const [openCustomerMobilePhoneModal, setOpenCustomerMobilePhoneModal] = useState(false)

  const unmount = useRef(null)

  const validateCart = async () => {
    const previousOrder = null

    const problematicItems =
      await validateCartContents(
        order.items,
        order.combos,
        previousOrder
      )

    if (problematicItems.length === 0) return true

    setProblematicItems(problematicItems)

    setOpenCartValidationResultModal(true)

    setSending(false)

    return false
  }

  const getRequestBody = (customerMobilePhone) => {
    const oItems =
      order.items.map(item => ({
        asComboSet: false,
        optionId: item.optionId,
        foodId: item.foodId,
        quantity: item.quantity,
        systemDiscount: item.systemDiscount * item.quantity,
        specificationItemIds:
          item.specificationItems.map(si => ({
            _id: si._id,
            quantity: si.quantity,
            builderStepKey: si.builderStepKey,
            portion: si.portion
          }))
      }))

    const cItems = []
    order.combos.forEach((combo, cIndex) =>
      combo.items.forEach(comboItem =>
        cItems.push({
          foodId: comboItem.foodId,
          optionId: comboItem.optionId,
          quantity: comboItem.quantity,
          specificationItemIds: [],
          comboSet: {
            code: combo.code,
            groupNumber: cIndex + 1,
            comboSetItemId: comboItem.comboSetItemId
          }
        })
      )
    )

    const items = [...cItems, ...oItems]

    const data = {
      items,
      isCod: true,
      isPrinted: true,
      deliveryType,
      branchId: cashierBranchId,
      couponCodes: [],
      deliveryAddress,
      areaName,
      paymentType: "cod",
      deliveryService: "internal",
      customerMobilePhone
    }

    const shiftId =
      shiftCashier[accountId]._id

    if (deliveryType === "dineIn") {
      data["tableNumber"] = "kiosk"
    }

    if (deliveryType === "cinema") {
      data["cinemaSolonNumber"] = cinemaSolonNumber
    }

    if (shiftId) {
      data["shiftId"] = shiftId
    }

    return data
  }

  const handlePlaceOrder = async (customerMobilePhone) => {
    setSending(true)

    try {
      const result = await validateCart()

      if (!result) return

      let accountId = null

      if (customerMobilePhone) {
        const response =
          await addUser({
            registeredFromBranchId: cashierBranchId,
            fullName: "",
            mobilePhone: customerMobilePhone,
            note: ""
          })

        accountId = response._id
      }

      const requestBody =
        getRequestBody(customerMobilePhone)

      const { order } =
        await sendOrder(
          requestBody,
          accountId ? { accountId } : {}
        )

      setOrderCode(order.code)

      setOrderLink(order._id)

      printOrder(order)

      if (unmount.current) return

      setSending(false)

      setResultModal("success")
    } catch (err) {
      console.log(err)

      if (unmount.current) return

      setSending(false)

      setResultModal("fail")
    }
  }

  const handleClearCart = () => {
    dispatch(
      deleteAllOrderItems()
    )
  }

  useEffect(() => {
    unmount.current = false

    return () => { unmount.current = true }
  }, [])

  return (
    <Fragment>
      <Button
        fullWidth
        disabled={temporaryOff}
        loading={sending}
        className={classes["complete"]}
        onClick={() => setOpenCustomerMobilePhoneModal(true)}
      >
        {t("ایجاد سفارش")}
      </Button>

      {
        resultModal === "success" &&
        <KpSuccess
          link={orderLink}
          onRedirect={handleClearCart}
          title={`${t("سفارش_شما_با_کد_موفقیت_ثبت_گردید_لطفا_جهت_پرداخت_به_صندوقدار_مراجعه_کنید", { code: orderCode })}.`}
        />
      }

      {
        resultModal === "fail" &&
        <KpFail />
      }

      <CartStockValidationResultModal
        problematicItems={problematicItems}
        openCartValidationResultModal={openCartValidationResultModal}
        setOpenCartValidationResultModal={setOpenCartValidationResultModal}
      />

      {
        openCustomerMobilePhoneModal &&
        <KcCustomerMobileModal
          openModal={openCustomerMobilePhoneModal}
          setOpenModal={setOpenCustomerMobilePhoneModal}
          onSkip={() => handlePlaceOrder("")}
          onMobilePhoneEnter={(customerMobilePhone) => handlePlaceOrder(customerMobilePhone)}
        />
      }
    </Fragment>
  )
}

export default KcPlaceOrderButton
